import React from 'react'

interface Props {
  children: React.ReactNode
  /**
   * Rendered instead of `children` after a render error. Kept as a prop so
   * each boundary can degrade to something useful for its own surface rather
   * than showing a generic apology.
   */
  fallback: React.ReactNode
  /** Optional hook for reporting to an error tracker. */
  onError?: (error: Error, info: React.ErrorInfo) => void
}

interface State {
  hasError: boolean
}

/**
 * Catches render-time errors in a subtree and shows `fallback` instead of
 * letting the error unmount the whole React tree.
 *
 * This exists because a single throw inside a top-level page component blanks
 * the document. On a tenant's public root — where the page IS the company's
 * web presence — that is a white screen on a listed company's domain.
 *
 * Only render errors are caught. Errors thrown from event handlers, async
 * callbacks, or effects that run after paint are not; those still need their
 * own try/catch at the throw site.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info)
    console.error('ErrorBoundary caught', error, info?.componentStack)
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children
  }
}

export default ErrorBoundary
